<script setup lang="ts">
import notificationStrings from '~/data/notificationStrings'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'
import type { Notification } from '~/types/notifications'

const { t, locale } = useI18n()
const mainStore = useMainStore()
const authStore = useAuthStore()
const { getItem, setItem } = useLocalStorageService()
const { getNotifications: getNotificationsFromAPI } = useUserService()
const { trackEvent } = useAnalyticsTrackingService()

const max = 6
const refreshIntervalMs = 60000

const state = reactive({
  expanded: false,
  lastRead: new Date(0).toISOString(),
  currentlyVisible: max,
  showAll: false
})

let refreshInterval: ReturnType<typeof setInterval>

const notifications = computed(() => mainStore.notifications)

const unread = computed(() =>
  notifications.value[0] ? state.lastRead < notifications.value[0].timestamp : false
)

const getNotifications = async () => {
  const userId = authStore.currentUser?.id

  if (userId === undefined) return

  let result: Notification[] | null | undefined

  try {
    result = await getNotificationsFromAPI(String(userId))
  } catch (err) {
    console.error('reqNotifications error:', (err as Error).message)
    throw err
  }

  if (!result) return

  const sorted = [...result].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

  const numberOfOldNotifications = mainStore.notifications.length

  mainStore.setNotifications(sorted)

  // Get the "time when notifications were last checked" from local storage
  if (import.meta.client) {
    state.lastRead = getItem('lastNotificationCheck_' + userId) || new Date(0).toISOString()
  }

  // While expanded, grow the visible count by however many notifications the new
  // request added, so the user keeps seeing the same items plus the new ones.
  // Note: the backend can drop older notifications when new ones arrive, so this
  // delta is only an approximation of "how many are new".
  if (state.expanded) {
    state.currentlyVisible += sorted.length - numberOfOldNotifications
  } else {
    // Ensure that at least all unread notifications become visible once expanded
    let i = 0
    while (i < sorted.length) {
      if (i >= max && sorted[i].timestamp < state.lastRead) {
        break
      }
      i++
    }
    if (i === sorted.length) {
      state.showAll = true
    }
    state.currentlyVisible = Math.max(max, i)
  }
}

const open = () => {
  trackEvent(ANALYTICS_EVENTS.notificationOverviewShow())

  getNotifications()
}

const close = () => {
  // Update last read for notifications
  state.lastRead = notifications.value[0] ? notifications.value[0].timestamp : new Date(0).toISOString()
  setItem('lastNotificationCheck_' + authStore.currentUser?.id, state.lastRead)

  // Reset some variables
  state.currentlyVisible = max
  state.showAll = false
}

const showMoreNotifications = () => {
  state.currentlyVisible += max
}

// Relative time ("1 minute ago" … "1 week ago") for the first week, then the
// absolute date. Both formatted with Intl using the active locale.
const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp)
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const absSeconds = Math.abs(diffSeconds)

  const minute = 60
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day

  if (absSeconds < 2 * week) {
    const relative = new Intl.RelativeTimeFormat(locale.value, { numeric: 'auto' })

    if (absSeconds < minute) {
      return relative.format(diffSeconds, 'second')
    } else if (absSeconds < hour) {
      return relative.format(Math.round(diffSeconds / minute), 'minute')
    } else if (absSeconds < day) {
      return relative.format(Math.round(diffSeconds / hour), 'hour')
    } else if (absSeconds < week) {
      return relative.format(Math.round(diffSeconds / day), 'day')
    } else {
      return relative.format(Math.round(diffSeconds / week), 'week')
    }
  }

  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium' }).format(date)
}

const notificationLink = (notification: Notification) => {
  switch (notification.type) {
    case 'GROUP_INVITE':
      return '/groups'

    case 'NEW_GROUP_POSTING':
      return `/groups/${notification.group_id}/posts/${notification.posting_id}`

    case 'NEW_GROUP_POSTING_COMMENT':
      return `/groups/${notification.group_id}/posts/${notification.posting_id}`

    case 'NEW_POSTING':
      return `/postings/${notification.posting_id}`

    case 'NEW_POSTING_COMMENT':
      return `/postings/${notification.posting_id}`

    case 'REWIND_CREATED':
      return `/trips/${notification.trip_id}`
  }

  // TODO handle other notification types via notification.type as well

  if (notification.bike_id) {
    return '/bike/' + notification.bike_id
  } else if (notification.trip_id) {
    return '/trips/' + notification.trip_id
  } else if (notification.group_id) {
    return '/groups/' + notification.group_id
  } else if (notification.getaway_id) {
    return '/getaways/' + notification.getaway_id
  } else if (notification.user_id) {
    return '/user/' + notification.user_id
  }
}

onMounted(() => {
  getNotifications()

  refreshInterval = setInterval(getNotifications, refreshIntervalMs)
})

onBeforeUnmount(() => {
  clearInterval(refreshInterval)
})
</script>

<template>
  <NavigationTopBarItemBase @close="close" @open="open">
    <template #button>
      <div class="tw-relative">
        <FontAwesomeIcon icon="fa-bell fa-regular" />

        <div
          v-if="unread"
          class="tw-absolute tw-right-0 tw-top-0 tw-h-2 tw-w-2 tw-rounded-full tw-bg-riser-red"
        />
      </div>
    </template>

    <template #default="{ triggerClose }">
      <div
        class="tw-w-full tw-overflow-y-auto md:tw-w-96"
        style="
          min-height: 50px;
          max-height: min(80vh, 450px);
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
        "
      >
        <div>
          <div
            v-for="notification of notifications.slice(0, state.currentlyVisible)"
            :key="'notification' + notification.gid"
            class="tw-flex tw-items-center tw-border-b tw-border-black/10 tw-px-4 tw-py-3 tw-text-sm last:tw-border-b-0"
            :class="{
              'tw-bg-riser-blue/5': state.lastRead < notification.timestamp
            }"
          >
            <NuxtLink
              class="tw-block tw-h-10 tw-w-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full"
              :to="'/user/' + notification.user_id"
              @click="triggerClose"
            >
              <RelationshipUserImage
                :user="notification.user"
                class="tw-h-full tw-w-full tw-object-cover"
              />
            </NuxtLink>

            <NuxtLink :to="notificationLink(notification)" class="tw-ml-4" @click="triggerClose">
              <i18n-t
                scope="global"
                tag="p"
                class="tw-m-0 tw-block"
                :keypath="notificationStrings[notification.type] ?? '-'"
              >
                <span v-if="notification.type === 'GETAWAY_REMINDER'">
                  <span>{{ notification.user.firstname }} {{ notification.user.lastname }}</span>
                  <span v-if="notification.title"> ({{ notification.title }})</span>
                </span>

                <span
                  v-else-if="!['GROUP_YOU_JOINED', 'REWIND_CREATED'].includes(notification.type)"
                >
                  <span>{{ notification.user.firstname }} {{ notification.user.lastname }}</span>
                </span>

                <span>{{ notification.title || '' }}</span>
              </i18n-t>

              <time
                :datetime="notification.timestamp"
                class="tw-mt-0.5 tw-block tw-text-xs tw-text-riser-secondaryText"
              >
                {{ formatTimestamp(notification.timestamp) }}
              </time>
            </NuxtLink>
          </div>
        </div>

        <div class="tw-flex tw-justify-center">
          <div
            v-if="!state.showAll && notifications.length > state.currentlyVisible"
            class="tw-cursor-pointer tw-py-2 tw-text-sm tw-text-riser-blue"
            @click="state.showAll = true"
          >
            {{ t('More') }}
          </div>
        </div>

        <LoadMoreButton
          v-if="state.showAll && notifications.length > state.currentlyVisible"
          @click="showMoreNotifications"
          @became-visible="showMoreNotifications"
        >
          <div class="tw-flex tw-justify-center">
            <div class="tw-cursor-pointer tw-py-2 tw-text-sm tw-text-riser-blue">
              {{ t('More') }}
            </div>
          </div>
        </LoadMoreButton>
      </div>
    </template>
  </NavigationTopBarItemBase>
</template>
